import { useNavigate } from "react-router-dom";
import { useAppSelector } from "store/hooks";
import { companyMetadataSelector, userMetadataSelector } from "store/selectors";

import { UserRole } from "types/BETypes";
import { IUserWithCompany } from "types/user";
import { hasAdminRights, hasEmployeeRights } from "permissions/helpers/shared";
import { isAdminOnboarded, isWorkerOnboarded } from "helpers";
import { hasAccessToInternalBankAccount } from "helpers/bankAccount/internalBankAccount";
import {
  getShouldAdminCompleteSignUp,
  getShouldEmployeeCompleteSignUp,
  getShouldOwnerCompleteSignUp,
} from "helpers/shared/guardRedirect";
import { hasAdvancedAdminBankingRights } from "pages/Banking/shared/permissions";

import routes from "./routes";
import {
  TRouteBankingDisableCondition,
  TRouteCondition,
  TRouteDisableCondition,
  TRouteResult,
} from "./types";

/* TODO::: Remake block conditions
It'll be better to use conditions in which routes are allowed to be visited, not the other way around */

export const getEmployeePagesBlockCondition: TRouteDisableCondition = (user) => {
  return hasEmployeeRights(user) && !isWorkerOnboarded(user);
};

export const getAdminPagesBlockCondition: TRouteDisableCondition = (user, company) => {
  return hasAdminRights(user) && !isAdminOnboarded(company);
};

export const getSharedPagesBlockCondition: TRouteDisableCondition = (user, company) => {
  return getEmployeePagesBlockCondition(user) || getAdminPagesBlockCondition(user, company);
};

export const shouldShowEmployeeOnboardingPage: TRouteCondition = (user, company) => {
  return !isWorkerOnboarded(user);
};

export const shouldShowAdminOnboardingPage: TRouteCondition = (user, company) => {
  return !!company?.showSuccessfulOnboarding || !company?.isOnboardingComplete;
};

//NOTE:::Banking
export const getAdminBankingBlockCondition: TRouteBankingDisableCondition = (
  user,
  company,
  bankCards,
) => {
  return (
    !hasAdminRights(user) ||
    !isAdminOnboarded(company) ||
    !hasAccessToInternalBankAccount(user, company, bankCards)
  );
};

export const getEmployeeBankingBlockCondition: TRouteBankingDisableCondition = (
  user,
  company,
  bankCards,
) => {
  return (
    !hasEmployeeRights(user) ||
    !isWorkerOnboarded(user) ||
    !hasAccessToInternalBankAccount(user, company, bankCards)
  );
};

export const getBankingBlockCondition: TRouteBankingDisableCondition = (
  user,
  company,
  bankCards,
) => {
  if (hasAdminRights(user)) {
    return getAdminBankingBlockCondition(user, company, bankCards);
  } else if (hasEmployeeRights(user)) {
    return getEmployeeBankingBlockCondition(user, company, bankCards);
  }

  return true;
};

export const getBankingAdvancedBlockCondition: TRouteBankingDisableCondition = (user, company) => {
  return (
    getBankingBlockCondition(user, company) ||
    (hasAdminRights(user) && !hasAdvancedAdminBankingRights(user))
  );
};

//NOTE:::Route helpers
export const useRouteHelpers = () => {
  const currentUser = useAppSelector(userMetadataSelector);
  const currentCompany = useAppSelector(companyMetadataSelector);
  const navigate = useNavigate();

  const _getEmployeePagesBlockCondition: TRouteDisableCondition = (
    user = currentUser,
    company = currentCompany,
  ) => {
    return getEmployeePagesBlockCondition(user, company);
  };

  const _getAdminPagesBlockCondition: TRouteDisableCondition = (
    user = currentUser,
    company = currentCompany,
  ) => {
    return getAdminPagesBlockCondition(user, company);
  };

  const getDefaultEmployeeRoute: TRouteResult = (user = currentUser, company = currentCompany) => {
    if (!user || !company) return routes.ROOT;
    return shouldShowEmployeeOnboardingPage(user, company)
      ? routes.EMPLOYEE_SETUP
      : routes.EMPLOYEE_PAY;
  };

  const getDefaultAdminRoute: TRouteResult = (user = currentUser, company = currentCompany) => {
    if (!user || !company) return routes.ROOT;
    return shouldShowAdminOnboardingPage(user, company) ? routes.ADMIN_SETUP : routes.ADMIN_PAY;
  };

  const getDefaultRoute: TRouteResult = (user = currentUser, company = currentCompany) => {
    if (!user) return routes.ROOT;
    return user?.activeCompanyUser?.lastActiveRole === UserRole.EMPLOYEE
      ? getDefaultEmployeeRoute(user, company)
      : getDefaultAdminRoute(user, company);
  };

  const redirectByRoleAndSignUpStatus = async (
    userWithCompany: IUserWithCompany | undefined,
    companiesIds: string[],
  ) => {
    const { user, company } = userWithCompany || {};

    const shouldEmployeeCompleteSignUp = getShouldEmployeeCompleteSignUp(user);
    const shouldAdminCompleteSignUp = getShouldAdminCompleteSignUp(user);
    const shouldOwnerCompleteSignUp = getShouldOwnerCompleteSignUp(userWithCompany, companiesIds);
    const shouldUserSelectCompany = companiesIds.length > 1 && !user?.activeCompanyId;

    if (!shouldAdminCompleteSignUp && !shouldEmployeeCompleteSignUp && !shouldOwnerCompleteSignUp) {
      return navigate(getDefaultRoute());
    }
    if (shouldUserSelectCompany) return navigate(routes.SELECT_COMPANY);

    if (shouldEmployeeCompleteSignUp) {
      //NOTE:::should to redirect employee to complete sign up if he didn't accept terms
      navigate(routes.EMPLOYEE_SIGN_UP);
    } else if (shouldOwnerCompleteSignUp) {
      //NOTE:::if user is an owner and company doesn't exist, redirect to company sign up
      navigate(routes.SIGN_UP_COMPLETE, { replace: true });
    } else if (shouldAdminCompleteSignUp) {
      //NOTE:::should to redirect ADMIN and SUPERADMIN to complete sign up if he didn't fill in the first and last name
      navigate(routes.ADMIN_SIGN_UP, { replace: true });
    }
  };

  return {
    getEmployeePagesBlockCondition: _getEmployeePagesBlockCondition,
    getAdminPagesBlockCondition: _getAdminPagesBlockCondition,
    redirectByRoleAndSignUpStatus,
    getDefaultEmployeeRoute,
    getDefaultAdminRoute,
    getDefaultRoute,
    isAdminOnboarded,
    isWorkerOnboarded,
    shouldShowEmployeeOnboardingPage,
    shouldShowAdminOnboardingPage,
  };
};
